import { Box, Drawer, Group, Stack, Text, UnstyledButton, rem } from '@mantine/core';
import P4Regular from 'components/typography/P4Regular';
import NavbarItem from 'layouts/dashboard/navbar/NavbarItem';
import {
  DashboardNavigationGroupKey,
  DashboardNavigationSection,
  getDashboardActiveSection,
  useDashboardNavigation,
} from 'layouts/dashboard/navigation';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Dashboard bottom navigation for mobile screens.
 */
export default function MobileBottomNavigation() {
  const sections = useDashboardNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const [openedSectionKey, setOpenedSectionKey] = useState<DashboardNavigationGroupKey | null>(null);

  const activeSection = useMemo(() => getDashboardActiveSection(location.pathname), [location.pathname]);
  const openedSection = useMemo(
    () =>
      sections.find(
        (section): section is DashboardNavigationSection & { key: DashboardNavigationGroupKey } =>
          section.key === openedSectionKey
      ) ?? null,
    [openedSectionKey, sections]
  );

  return (
    <>
      <Box
        data-e2e-capture-role="mobile-navigation"
        pos="fixed"
        left={0}
        right={0}
        bottom={0}
        style={{ zIndex: 201 }}
        h="calc(72px + env(safe-area-inset-bottom))"
        bg="white"
        px={8}
        pt={8}
        pb="calc(8px + env(safe-area-inset-bottom))"
        sx={(theme) => ({
          borderTop: `1px solid ${theme.colors.gray[1]}`,
          boxShadow: '0px -8px 24px rgba(40, 41, 52, 0.06)',
        })}
      >
        <Group grow align="stretch" spacing={4} h="100%" noWrap>
          {sections.map((section) => {
            const isActive = activeSection === section.key;

            return (
              <UnstyledButton
                key={section.key}
                aria-pressed={isActive}
                data-active={isActive}
                onClick={() => {
                  if (section.to) {
                    setOpenedSectionKey(null);
                    navigate(section.to);
                    return;
                  }

                  const nextSectionKey = section.key as DashboardNavigationGroupKey;

                  setOpenedSectionKey((current) => (current === nextSectionKey ? null : nextSectionKey));
                }}
                style={{ height: '100%' }}
              >
                <Stack
                  spacing={4}
                  align="center"
                  justify="center"
                  h="100%"
                  sx={(theme) => ({
                    borderRadius: rem(8),
                    color: isActive ? theme.colors.red[6] : theme.colors.gray[7],
                    backgroundColor: isActive ? theme.colors.red[0] : 'transparent',
                  })}
                >
                  <Box>{section.icon}</Box>
                  <P4Regular color={isActive ? 'red.6' : 'gray.7'}>{section.mobileLabel}</P4Regular>
                </Stack>
              </UnstyledButton>
            );
          })}
        </Group>
      </Box>

      <Drawer
        opened={!!openedSection}
        onClose={() => setOpenedSectionKey(null)}
        position="bottom"
        withCloseButton={false}
        padding={16}
        size="auto"
        styles={{
          content: {
            borderTopLeftRadius: rem(16),
            borderTopRightRadius: rem(16),
          },
          body: {
            paddingBottom: `calc(${rem(16)} + env(safe-area-inset-bottom))`,
          },
        }}
      >
        {openedSection && (
          <Stack spacing={12}>
            <Text c="gray.7" fw={500}>
              {openedSection.label}
            </Text>
            <Stack spacing={4}>
              {openedSection.items.map((item) => (
                <Box key={item.to} onClick={() => setOpenedSectionKey(null)}>
                  <NavbarItem to={item.to} label={item.label} icon={item.icon} isCollapsed={false} alwaysShowLabel />
                </Box>
              ))}
            </Stack>
          </Stack>
        )}
      </Drawer>
    </>
  );
}
